import React from "react";
import ServiceLandingPage, {
  InternalLink,
} from "../components/ServiceLandingPage";
import { COMPANY } from "../content";

const SCOPE = [
  {
    name: "Demolition & civil works",
    desc: "Full strip-out, structural changes, partition walls, plastering, waterproofing and floor levelling — handled by our own civil team across the UAE.",
  },
  {
    name: "MEP upgrades (electrical, plumbing, HVAC)",
    desc: "New circuits, lighting design, plumbing reroutes, AC re-zoning and ventilation upgrades — coordinated with DEWA, SEWA and developer requirements.",
  },
  {
    name: "Bespoke joinery & built-in furniture",
    desc: "Wardrobes, kitchens, vanities, walk-in closets, TV units and feature panels — designed and manufactured at our Sharjah atelier.",
  },
  {
    name: "Marble, stone & wall finishes",
    desc: "Calacatta islands, microcement, decorative wallpaper, fluted panels and feature walls — sourced and installed to villa-grade specification.",
  },
  {
    name: "Lighting, ceilings & smart home",
    desc: "Cove lighting, designer fixtures, smart switches, motorized curtains and Sonos/Lutron integration where required.",
  },
  {
    name: "Furniture, upholstery & styling",
    desc: "Custom sofas, headboards, dining tables and rugs from our atelier — plus on-site styling, art curation and final handover photography.",
  },
];

const PROCESS = [
  {
    n: "01",
    title: "Site visit & condition survey",
    body: "We meet you at your villa, assess existing finishes, MEP and structural conditions, take measurements and listen to how the home needs to evolve.",
  },
  {
    n: "02",
    title: "Design, BOQ & transparent quote",
    body: "Concept mood-boards, layout drawings, material selections and an itemised BOQ — so the renovation budget is clear before any work begins.",
  },
  {
    n: "03",
    title: "Approvals & access coordination",
    body: "We handle Dubai Municipality, Trakheesi, community (Emaar, Nakheel, Meraas, Damac) and security approvals, deposits and access scheduling.",
  },
  {
    n: "04",
    title: "Phased on-site execution",
    body: "Demolition → civil → MEP first-fix → joinery installation → finishes → second-fix → snag — managed by a single project lead, not a chain of subcontractors.",
  },
  {
    n: "05",
    title: "Snag, deep clean & handover",
    body: "Final snag list, professional deep clean, walkthrough and a defects-warranty period — so the villa is move-in ready, not just 'finished'.",
  },
];

const FAQS = [
  {
    q: "How long does a villa renovation take in Dubai?",
    a: "A standard 4–5 bedroom villa renovation in Dubai typically takes 12–20 weeks of on-site execution, plus 2–3 weeks for design, BOQ and approvals up front. Faster timelines are possible for partial renovations (kitchen + bathrooms only) or where occupancy isn't required during the works.",
  },
  {
    q: "How much does villa renovation cost in Dubai?",
    a: "Villa renovation cost in Dubai typically ranges from AED 350 to AED 1,500 per sq.ft depending on scope (cosmetic refresh vs full strip-out), finish quality (standard vs premium marble, joinery, lighting) and whether MEP upgrades are required. Time Furniture provides itemised quotations after a free site visit.",
  },
  {
    q: "Do you handle community and authority approvals?",
    a: "Yes. We coordinate Dubai Municipality, Trakheesi, Dubai Civil Defence, and community-developer approvals (Emaar, Nakheel, Meraas, Damac, Dubai Hills, Arabian Ranches, Palm Jumeirah, etc.) as part of every villa renovation contract.",
  },
  {
    q: "Can you renovate while we're still living in the villa?",
    a: "We strongly recommend full vacancy for major renovations involving demolition, MEP and joinery installation — for safety, dust control and timeline reasons. For partial renovations (one wing, one floor, or kitchen-only), phased occupancy is possible and we'll plan dust barriers and access accordingly.",
  },
  {
    q: "Do you also supply furniture and styling?",
    a: "Yes. Time Furniture is a full turnkey contractor — we manufacture custom furniture and upholstery in-house and provide final styling, art curation and handover-photography styling, so the villa is fully completed and not just structurally finished.",
  },
];

const WHY = [
  {
    t: "End-to-end villa specialists",
    d: "Civil, MEP, joinery, marble, lighting, furniture, upholstery and styling — one accountable team from demolition to handover.",
  },
  {
    t: "30+ years in UAE villas",
    d: "Established 1993. Three decades of villa renovations across Palm Jumeirah, Emirates Hills, Dubai Hills, Al Barari and Mohammed Bin Rashid City.",
  },
  {
    t: "In-house atelier",
    d: "Sharjah-based joinery and upholstery facility — no third-party supply risk, full control over quality and lead times.",
  },
];

export default function VillaRenovationDubai() {
  return (
    <ServiceLandingPage
      testid="villa-renovation-dubai-page"
      whatsappText={COMPANY.whatsappVillaText}
      pageTitle="Villa Renovation Dubai | Turnkey Villa Renovation Contractor — Time Furniture"
      metaDescription="Luxury villa renovation in Dubai — turnkey strip-out, MEP, joinery, marble, lighting and furnishing under one accountable team. UAE villa renovation specialist since 1993."
      canonical="https://timefurniture.ae/villa-renovation-dubai"
      ogImage="/media/owner-projects/jumeirah-islands-villa-living-room-custom-sofas-time-furniture.webp"
      heroImage="/media/owner-projects/jumeirah-islands-villa-living-room-custom-sofas-time-furniture.webp"
      heroImageAlt="Custom sofas in a Jumeirah Islands villa living room renovated by Time Furniture"
      serviceSchemaName="Villa Renovation — Dubai & UAE"
      breadcrumbLabel="Villa Renovation Dubai"
      eyebrow="Villa Renovation · Dubai & UAE"
      h1="Luxury Villa Renovation in"
      h1Accent="Dubai & UAE"
      intro="Time Furniture is a turnkey villa renovation contractor in Dubai delivering complete, single-contract execution for villas, townhouses and large apartments. Since 1993, we have renovated homes across Palm Jumeirah, Emirates Hills, Dubai Hills, Al Barari, Jumeirah Golf Estates, Mohammed Bin Rashid City and the wider UAE — handling demolition, MEP, marble, joinery, custom furniture, curtains and final styling under one accountable team. Whether the brief is a full strip-out and rebuild or a focused kitchen-and-bathroom refresh, we provide transparent BOQs, manage all community and authority approvals, and execute phased on-site works to a fixed handover date — so you receive a fully completed, move-in-ready villa, not just a structurally finished one."
      scope={SCOPE}
      process={PROCESS}
      whyChoose={WHY}
      faqs={FAQS}
      featureGrid={
        <section data-testid="villa-recent-work" className="max-w-[1400px] mx-auto px-6 md:px-12 py-16 md:py-24">
          <p className="eyebrow mb-4">Recent villa work</p>
          <h2 className="font-serif-display text-3xl md:text-4xl text-[#1C110C] max-w-2xl">
            A Jumeirah Islands villa, renovated room by room.
          </h2>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
            <figure className="md:col-span-2">
              <img
                src="/media/owner-projects/jumeirah-islands-villa-dining-room-time-furniture.webp"
                width={1280} height={597} loading="lazy" decoding="async"
                alt="Dining room fit-out in a Jumeirah Islands villa by Time Furniture"
                data-testid="villa-recent-img-dining"
                className="w-full aspect-[16/9] object-cover object-center bg-[#EDE1C8]"
              />
              <figcaption className="mt-2 text-[10px] tracking-[0.14em] uppercase text-[#3E2A22]">Dining room</figcaption>
            </figure>
            <figure className="md:row-span-2">
              <img
                src="/media/owner-projects/jumeirah-islands-villa-marble-bathroom-time-furniture.webp"
                width={1500} height={2000} loading="lazy" decoding="async"
                alt="Marble bathroom with freestanding bath in a Jumeirah Islands villa by Time Furniture"
                data-testid="villa-recent-img-bathroom"
                className="w-full md:h-full aspect-[3/4] md:aspect-auto object-cover object-center bg-[#EDE1C8]"
              />
              <figcaption className="mt-2 text-[10px] tracking-[0.14em] uppercase text-[#3E2A22]">Marble bathroom</figcaption>
            </figure>
            <figure className="md:col-span-2">
              <img
                src="/media/owner-projects/jumeirah-islands-villa-bedroom-interior-time-furniture.webp"
                width={1280} height={597} loading="lazy" decoding="async"
                alt="Completed bedroom interior in a Jumeirah Islands villa by Time Furniture"
                data-testid="villa-recent-img-bedroom"
                className="w-full aspect-[16/9] object-cover object-center bg-[#EDE1C8]"
              />
              <figcaption className="mt-2 text-[10px] tracking-[0.14em] uppercase text-[#3E2A22]">Bedroom</figcaption>
            </figure>
          </div>
        </section>
      }
      ctaHeading="Ready to renovate your"
      ctaAccent="villa in Dubai"
      ctaSubtext="Book a free site visit. We'll review the villa, propose a phased renovation plan and reply with a transparent BOQ — no pressure, no obligation."
      internalLinksLine={
        <>
          Also see our{" "}
          <InternalLink to="/services">full interior fit-out services</InternalLink>
          ,{" "}
          <InternalLink to="/portfolio">completed villa renovation projects</InternalLink>
          ,{" "}
          <InternalLink to="/ongoing-projects">our ongoing villa projects</InternalLink>
          , or our{" "}
          <InternalLink to="/interior-fit-out-dubai">Dubai interior fit-out company page</InternalLink>
          .
        </>
      }
    />
  );
}
